import { useBrand } from "@branding/useBrand";
import { AppCta } from "../MarketingLayout";

/** About page: who the product is for and the principle behind it. */
export function About() {
  const brand = useBrand();

  return (
    <>
      <section className="mk-section narrow mk-prose">
        <div className="mk-eyebrow">About</div>
        <h1 className="mk-h1">Built for finance teams that keep their own books.</h1>
        <p className="mk-lead">
          {brand.name} exists for one job: give a small UAE finance team a single
          source of truth that is always tie-out correct.
        </p>

        <h2>Why we built it</h2>
        <p>
          UAE finance teams — often one to four people — have to keep proper
          double-entry books while a tax regime has become materially harder in a
          short window: 5% VAT since 2018, corporate tax at 9% for financial years
          from June 2023, and e-invoicing arriving now. Too many teams juggle
          spreadsheets alongside a general tool that isn’t purpose-fit for the UAE,
          and they lack a trustworthy, always-current view of their books.
        </p>
        <p>
          When they need a trial balance, P&amp;L, or balance sheet — for a bank,
          an auditor, or a tax filing — assembling one that actually ties out is
          slow and error-prone, and multi-currency makes it harder. {brand.name}
          {" "}was built so that entering transactions immediately produces an
          accurate, reconciling trial balance and financial statements, with VAT
          and corporate-tax categorization already attached.
        </p>

        <h2>What we believe</h2>
        <ul>
          <li>
            <strong>Tie-out is non-negotiable.</strong> Debits must equal credits
            at the point of posting, so the books can’t quietly drift out of
            balance.
          </li>
          <li>
            <strong>Compliance belongs at the source.</strong> VAT and
            corporate-tax categorization should be attached as you work, not
            reconstructed at filing time.
          </li>
          <li>
            <strong>Coexist, don’t force a switch.</strong> Import and export mean
            you can adopt alongside your current tool instead of migrating on day
            one.
          </li>
          <li>
            <strong>Be honest about scope.</strong> We tell you plainly what’s a
            feature today and what’s on the roadmap — including where accredited
            FTA e-invoicing and VAT 201 generation still sit.
          </li>
        </ul>

        <h2>Who it’s for</h2>
        <p>
          The small finance team inside an SME or mid-market UAE company that keeps
          its own books and wants control and real-time visibility — not a black
          box, and not a full ERP. Comfortable with debits and credits, and tired
          of not trusting the numbers.
        </p>
      </section>

      <section className="mk-cta">
        <div className="mk-section narrow">
          <h2 className="mk-h2">See {brand.name} against your own numbers.</h2>
          <div className="mk-hero-cta">
            <AppCta className="mk-btn primary lg">Start your free trial</AppCta>
          </div>
        </div>
      </section>
    </>
  );
}
